import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Edit2, ChevronDown, Trash2 } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useRef } from "react";

import { useAuth } from "@/features/auth/hooks/useAuth";
import { updateProfile, uploadPhoto, deleteAccount, UpdateProfilePayload } from "../api/profile";
import Container from "@/components/ui/Container";

export function EditProfileForm() {
    const { user, logout } = useAuth();
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const { register, handleSubmit, reset } = useForm({
        defaultValues: {
            first_name: "",
            last_name: "",
            username: "",
            phone_number: "",
            gender: "",
            country: "",
            address_line_1: "",
            address_line_2: "",
            travel_preferences: "",
            tour_style: "",
            interests: "",
            dietary_restrictions: "",
            cuisine_preferences: "",
            avoid_foods: "",
            accessibility_needs: "",
        },
    });

    useEffect(() => {
        if (user) {
            console.log("first_name", user?.first_name)
            reset({
                first_name: user?.first_name || "",
                last_name: user?.last_name || "",
                username: user?.username || "",
                phone_number: user?.phone_number || "",
                gender: user?.gender || "",
                country: user?.country || "",
                address_line_1: user?.address_line_1 || "",
                address_line_2: user?.address_line_2 || "",
                travel_preferences: user?.travel_preferences || "",
                tour_style: user?.tour_style || "",
                interests: user.interests?.join(", ") || "",
                dietary_restrictions: user.dietary_restrictions?.join(", ") || "",
                cuisine_preferences: user.cuisine_preferences?.join(", ") || "",
                avoid_foods: user.avoid_foods?.join(", ") || "",
                accessibility_needs: user.accessibility_needs?.join(", ") || "",
            });
        }
    }, [user, reset]);

    const mutation = useMutation({
        mutationFn: updateProfile,
        onSuccess: (data) => {
            toast.success("Profile updated successfully");
            queryClient.setQueryData(["auth", "user"], data);
            navigate({ to: "/profile" });
        },
        onError: (error) => {
            console.error(error);
            toast.error("Failed to update profile");
        },
    });

    const photoMutation = useMutation({
        mutationFn: uploadPhoto,
        onSuccess: (data) => {
            toast.success("Photo updated successfully");
            queryClient.setQueryData(["auth", "user"], data);
            queryClient.invalidateQueries({ queryKey: ["auth", "user"] });
        },
        onError: (error) => {
            console.error("Photo upload failed:", error);
            toast.error("Failed to upload photo");
        }
    });

    const deleteIdMutation = useMutation({
        mutationFn: deleteAccount,
        onSuccess: () => {
            toast.success("Account deleted successfully");
            logout();
        },
        onError: (error) => {
            console.error("Delete failed:", error);
            toast.error("Failed to delete account");
        }
    });

    const onSubmit = (data: any) => {
        const payload: UpdateProfilePayload = {
            ...data,
            interests: data.interests ? data.interests.split(',').map((s: string) => s.trim()).filter(Boolean) : [],
            dietary_restrictions: data.dietary_restrictions ? data.dietary_restrictions.split(',').map((s: string) => s.trim()).filter(Boolean) : [],
            cuisine_preferences: data.cuisine_preferences ? data.cuisine_preferences.split(',').map((s: string) => s.trim()).filter(Boolean) : [],
            avoid_foods: data.avoid_foods ? data.avoid_foods.split(',').map((s: string) => s.trim()).filter(Boolean) : [],
            accessibility_needs: data.accessibility_needs ? data.accessibility_needs.split(',').map((s: string) => s.trim()).filter(Boolean) : [],
        };
        mutation.mutate(payload);
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            photoMutation.mutate(file);
        }
    };

    const handleDeleteAccount = () => {
        if (confirm("Are you sure you want to delete your account? This action cannot be undone.")) {
            deleteIdMutation.mutate();
        }
    }

    const getInitials = () => {
        if (user?.first_name && user?.last_name) {
            return `${user.first_name[0]}${user.last_name[0]}`.toUpperCase();
        }
        return user?.username?.slice(0, 2).toUpperCase() || user?.email?.[0]?.toUpperCase() || "U";
    };

    // We use the profile_photo_url from the User object
    const avatarUrl = user?.profile_photo_url;

    return (
        <div className="bg-[#FFF8F3] min-h-screen pb-16">
            <div className="relative h-48 mb-16">
                <Container className="h-full relative">
                    <div className="absolute -bottom-12 left-0 right-0 flex justify-center items-end">
                        <div className="relative group">
                            <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-white bg-white flex items-center justify-center text-[#187A85] text-4xl font-bold shadow-md relative">
                                {avatarUrl ? (
                                    <img src={avatarUrl} alt="Profile" className="w-full h-full object-cover" />
                                ) : (
                                    getInitials()
                                )}
                                {photoMutation.isPending && (
                                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                                    </div>
                                )}
                            </div>
                            <button
                                type="button"
                                onClick={() => fileInputRef.current?.click()}
                                className="absolute bottom-0 right-0 bg-[#e55a28] p-2 rounded-full text-white shadow-lg hover:bg-[#d44e1e] transition-colors border-2 border-white"
                            >
                                <Edit2 size={16} />
                            </button>
                            <input
                                type="file"
                                ref={fileInputRef}
                                className="hidden"
                                accept="image/*"
                                onChange={handleFileChange}
                            />
                        </div>
                    </div>
                </Container>
            </div>

            <Container>
                <div className="text-center mb-10">
                    <h1 className="text-2xl font-bold text-gray-900">
                        {user?.first_name ? `${user.first_name} ${user.last_name}` : user?.username}
                    </h1>
                </div>

                <form onSubmit={handleSubmit(onSubmit)} className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6 p-4">

                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-medium text-gray-700">First Name</label>
                        <input
                            {...register("first_name")}
                            className="w-full p-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#187A85]/20 focus:border-[#187A85] transition-all bg-white text-gray-900"
                        />
                    </div>

                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-medium text-gray-700">Last Name</label>
                        <input
                            {...register("last_name")}
                            className="w-full p-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#187A85]/20 focus:border-[#187A85] transition-all bg-white text-gray-900"
                        />
                    </div>

                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-medium text-gray-700">Username</label>
                        <input
                            {...register("username")}
                            className="w-full p-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#187A85]/20 focus:border-[#187A85] transition-all bg-white text-gray-900"
                        />
                    </div>

                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-medium text-gray-700">Gender</label>
                        <div className="relative">
                            <select
                                {...register("gender")}
                                className="w-full p-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#187A85]/20 focus:border-[#187A85] transition-all bg-white text-gray-900 appearance-none"
                            >
                                <option value="">Select Gender</option>
                                <option value="male">Male</option>
                                <option value="female">Female</option>
                                <option value="other">Other</option>
                            </select>
                            <ChevronDown className="absolute right-3 top-3 text-gray-400 pointer-events-none" size={20} />
                        </div>
                    </div>

                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-medium text-gray-700">Email</label>
                        <input
                            value={user?.email || ""}
                            disabled
                            className="w-full p-3 rounded-lg border border-gray-200 bg-gray-50 text-gray-500 cursor-not-allowed"
                        />
                    </div>

                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-medium text-gray-700">Phone Number</label>
                        <input
                            {...register("phone_number")}
                            className="w-full p-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#187A85]/20 focus:border-[#187A85] transition-all bg-white text-gray-900"
                        />
                    </div>

                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-medium text-gray-700">Country</label>
                        <div className="relative">
                            <select
                                {...register("country")}
                                className="w-full p-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#187A85]/20 focus:border-[#187A85] transition-all bg-white text-gray-900 appearance-none"
                            >
                                <option value="">Select Country</option>
                                <option value="US">United States</option>
                                <option value="UK">United Kingdom</option>
                                <option value="CA">Canada</option>
                                <option value="DE">Germany</option>
                                <option value="FR">France</option>
                            </select>
                            <ChevronDown className="absolute right-3 top-3 text-gray-400 pointer-events-none" size={20} />
                        </div>
                    </div>

                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-medium text-gray-700">Address Line 1</label>
                        <input
                            {...register("address_line_1")}
                            className="w-full p-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#187A85]/20 focus:border-[#187A85] transition-all bg-white text-gray-900"
                        />
                    </div>
                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-medium text-gray-700">Address Line 2</label>
                        <input
                            {...register("address_line_2")}
                            className="w-full p-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#187A85]/20 focus:border-[#187A85] transition-all bg-white text-gray-900"
                        />
                    </div>

                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-medium text-gray-700">Travel Preference</label>
                        <input
                            {...register("travel_preferences")}
                            placeholder="e.g. Hiking, Beach, Cultural..."
                            className="w-full p-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#187A85]/20 focus:border-[#187A85] transition-all bg-white text-gray-900"
                        />
                    </div>

                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-medium text-gray-700">Tour Style</label>
                        <input
                            {...register("tour_style")}
                            placeholder="e.g. Budget, Luxury..."
                            className="w-full p-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#187A85]/20 focus:border-[#187A85] transition-all bg-white text-gray-900"
                        />
                    </div>

                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-medium text-gray-700">Interests</label>
                        <input
                            {...register("interests")}
                            placeholder="Comma separated e.g. History, Art, Food"
                            className="w-full p-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#187A85]/20 focus:border-[#187A85] transition-all bg-white text-gray-900"
                        />
                    </div>

                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-medium text-gray-700">Dietary Restrictions</label>
                        <input
                            {...register("dietary_restrictions")}
                            placeholder="Comma separated e.g. Vegan, Nut-free"
                            className="w-full p-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#187A85]/20 focus:border-[#187A85] transition-all bg-white text-gray-900"
                        />
                    </div>

                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-medium text-gray-700">Cuisine Preferences</label>
                        <input
                            {...register("cuisine_preferences")}
                            placeholder="Comma separated e.g. Italian, Thai"
                            className="w-full p-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#187A85]/20 focus:border-[#187A85] transition-all bg-white text-gray-900"
                        />
                    </div>

                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-medium text-gray-700">Avoid Foods</label>
                        <input
                            {...register("avoid_foods")}
                            placeholder="Comma separated"
                            className="w-full p-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#187A85]/20 focus:border-[#187A85] transition-all bg-white text-gray-900"
                        />
                    </div>

                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-medium text-gray-700">Accessibility Needs</label>
                        <input
                            {...register("accessibility_needs")}
                            placeholder="Comma separated"
                            className="w-full p-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#187A85]/20 focus:border-[#187A85] transition-all bg-white text-gray-900"
                        />
                    </div>

                    <div className="col-span-1 md:col-span-2 flex justify-center mt-6 gap-4">
                        <button
                            type="submit"
                            disabled={mutation.isPending}
                            className="bg-[#e55a28] hover:bg-[#d44e1e] text-white px-12 py-3 rounded-lg font-bold shadow-md transition-colors disabled:opacity-70 w-1/2"
                        >
                            {mutation.isPending ? "Saving..." : "Submit"}
                        </button>
                    </div>
                </form>
            </Container>
        </div>
    );
}
